import { Icon } from '@natera/material/lib/icon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { cn } from '~/shared/lib/cn'
import { Button } from '~/shared/ui/button'
import { Chip } from '~/shared/ui/chip'
import { LocalIcon } from '~/shared/ui/local-icon'
import { Text } from '~/shared/ui/text'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command'
import { MultiSelectOptionMemoized } from './multi-select-option'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import type { Option } from './types'

interface MultiSelectProps<T> {
  className?: string
  label: React.ReactNode
  options: Option<T>[]
  value?: T[]
  onValueChange: (value: T[]) => void
  placeholder?: string
  required?: boolean
  allSelectable?: boolean
  frequentValues?: T[]
  frequentGroupLabel?: string
  recommendedValues?: T[]
  recommendedGroupLabel?: string
  hint?: string
  disabled?: boolean
  error?: string
  /** Single-select mode: no checkboxes, replaces value on select, auto-closes. */
  mode?: 'single' | 'multi'
}

export function MultiSelect<T extends string>({
  className,
  label,
  options,
  value = [],
  onValueChange,
  placeholder = 'Select',
  required = false,
  allSelectable = false,
  frequentValues = [],
  frequentGroupLabel,
  recommendedValues = [],
  recommendedGroupLabel,
  hint,
  disabled = false,
  error,
  mode = 'multi',
}: MultiSelectProps<T>) {
  const isSingle = mode === 'single'
  const [selectedValues, setSelectedValues] = useState<T[]>(value)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const frequentOptions = frequentValues
    .map((value) => options.find((option) => option.value === value))
    .filter((option) => option !== undefined)

  const recommendedOptions = recommendedValues
    .map((value) => options.find((option) => option.value === value))
    .filter((option) => option !== undefined)

  useEffect(() => {
    setSelectedValues((prev) => (value.join(',') !== prev.join(',') ? value : prev))
  }, [value])

  const toggleOption = (option: T) => {
    if (isSingle) {
      const newValue = selectedValues.includes(option) ? [] : [option]
      onValueChange(newValue)
      setIsPopoverOpen(false)
      return
    }
    const newSelectedValues = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option]
    onValueChange(newSelectedValues)
  }

  const handleClear = () => {
    onValueChange([])
  }

  const handleTogglePopover = () => {
    setIsPopoverOpen((prev) => !prev)
  }

  useEffect(() => {
    if (isPopoverOpen) {
      requestAnimationFrame(() => {
        const input = searchInputRef.current
        if (input) {
          input.focus()
          const len = input.value.length
          input.setSelectionRange(len, len)
        }
      })
    }
  }, [isPopoverOpen])

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open)
    if (!open) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (isPopoverOpen) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsPopoverOpen(true)
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault()
      setSearchValue(e.key)
      setIsPopoverOpen(true)
    }
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const trimmed = searchValue.toLowerCase().trim()
      if (!trimmed) return
      const match = options.find((o) => o.value.toLowerCase() === trimmed)
      if (match) {
        toggleOption(match.value)
        setSearchValue('')
      }
    }
  }

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear()
    } else {
      const allValues = options.map((option) => option.value)
      onValueChange(allValues)
    }
  }

  const sortedSelectedValues = useMemo(() => {
    return selectedValues.sort((a, b) => {
      const aIsRecommended = recommendedValues.includes(a)
      const bIsRecommended = recommendedValues.includes(b)
      if (aIsRecommended && !bIsRecommended) return -1
      if (!aIsRecommended && bIsRecommended) return 1
      return a.localeCompare(b)
    })
  }, [selectedValues, recommendedValues])

  const sortedDeselectedRecommendedValues = useMemo(() => {
    return recommendedValues
      .filter((value) => !selectedValues.includes(value))
      .sort((a, b) => {
        return a.localeCompare(b)
      })
  }, [recommendedValues, selectedValues])

  const isLabelTransformed =
    isPopoverOpen || selectedValues.length > 0 || sortedDeselectedRecommendedValues.length > 0

  const visibleOptions = useMemo(
    () =>
      options
        .filter(
          (option) =>
            option.label.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
            option.description?.toLowerCase().includes(searchValue.toLowerCase().trim()),
        )
        .filter((option) => !(option?.hidden_from_search ?? false))
        .filter((o) => o.label.trim() !== ''),
    [options, searchValue],
  )

  const visibleFrequentOptions = useMemo(
    () =>
      frequentOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
          option.description?.toLowerCase().includes(searchValue.toLowerCase().trim()),
      ),
    [frequentOptions, searchValue],
  )

  const visibleRecommendedOptions = useMemo(
    () =>
      recommendedOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase().trim()) ||
          option.description?.toLowerCase().includes(searchValue.toLowerCase().trim()),
      ),
    [recommendedOptions, searchValue],
  )

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          data-focused={isFocused || undefined}
          className={cn(
            'group relative mt-2 flex h-auto min-h-[44px] w-full cursor-pointer items-center justify-between rounded-sm bg-inherit pl-4 hover:bg-inherit [&_svg]:pointer-events-auto',
            { 'mb-5': hint },
            { 'pointer-events-none': disabled },
            className,
          )}
          onClick={handleTogglePopover}
          onKeyDown={handleTriggerKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          {hint && (
            <Text
              className={cn(
                'text-text-secondary absolute bottom-0 left-0 translate-y-full px-4 py-0.5',
                {
                  'text-text-disabled': disabled,
                },
              )}
              appearance="CAPTION"
            >
              {hint}
            </Text>
          )}
          <div
            className={cn(
              '[&_div]:border-input-outlined-enabled-border pointer-events-none absolute top-0 left-0 flex h-full w-full [&_div]:h-full',
              {
                'group-data-[state=open]:[&_div]:border-input-outlined-focused-border group-data-[focused]:[&_div]:border-input-outlined-focused-border group-hover:[&_div]:border-input-outlined-hovered-border':
                  !error && !disabled,
                '[&_div]:border-input-outlined-disabled-border': disabled,
                '[&_div]:border-error-main': error,
              },
            )}
          >
            <div className="w-3 rounded-l-sm border-t border-b border-l group-data-[focused]:border-t-2 group-data-[focused]:border-b-2 group-data-[focused]:border-l-2 group-data-[state=open]:border-t-2 group-data-[state=open]:border-b-2 group-data-[state=open]:border-l-2" />
            <div className="w-auto border-t border-b group-data-[focused]:border-t-2 group-data-[focused]:border-b-2 group-data-[state=open]:border-t-2 group-data-[state=open]:border-b-2">
              <label
                className={cn(
                  'text-text-secondary bg-surface-lowest absolute top-1/2 left-3 flex -translate-y-1/2 px-1 text-[15px] leading-5 tracking-[0.25px] whitespace-nowrap transition-all duration-150 ease-in-out',
                  {
                    'text-primary-main': isPopoverOpen || isFocused,
                    'top-0 text-xs leading-4 tracking-[0.4px]': isLabelTransformed,
                    'text-text-disabled': disabled,
                    'text-error-main': error,
                  },
                )}
              >
                {label}
                {required && <span className="text-error-main">*</span>}
              </label>
            </div>
            <div className="grow rounded-r-sm border-t border-r border-b group-data-[focused]:border-t-2 group-data-[focused]:border-r-2 group-data-[focused]:border-b-2 group-data-[state=open]:border-t-2 group-data-[state=open]:border-r-2 group-data-[state=open]:border-b-2" />
          </div>
          {sortedSelectedValues.length > 0 || sortedDeselectedRecommendedValues.length > 0 ? (
            <div className="flex max-w-full flex-wrap items-center gap-2 overflow-hidden pt-4 pb-3">
              {sortedSelectedValues.map((value) => {
                const option = options.find((o) => o.value === value)
                if (!option) return null
                return (
                  <Chip
                    key={value}
                    outlined
                    selected
                    size="sm"
                    leadingIcon={
                      recommendedValues.includes(value) ? (
                        <LocalIcon
                          className="text-primary-dark"
                          name="opti"
                          size="18"
                        />
                      ) : null
                    }
                    onClose={() => {
                      toggleOption(value)
                    }}
                  >
                    {option.label}
                  </Chip>
                )
              })}
              {sortedDeselectedRecommendedValues.map((value) => {
                const option = options.find((o) => o.value === value)
                if (!option) return null
                return (
                  <Chip
                    key={value}
                    outlined
                    size="sm"
                    leadingIcon={
                      <LocalIcon
                        className="text-content-medium-emphasis"
                        name="optiSad"
                        size="18"
                      />
                    }
                    onClose={() => {}}
                    disabled
                  >
                    {option.label}
                  </Chip>
                )
              })}
            </div>
          ) : (
            isPopoverOpen && <Text appearance="BODY2">{placeholder}</Text>
          )}
          <Icon
            className={cn('text-text-secondary mx-3 cursor-pointer', {
              'rotate-180': isPopoverOpen,
              'ml-auto': !isLabelTransformed,
              'text-text-disabled': disabled,
            })}
            children="arrow_drop_down"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
        sideOffset={8}
      >
        <Command>
          <div className="border-divider flex items-center gap-3 border-b py-1 pr-2 pl-4">
            <Icon
              className="text-content-high-emphasis shrink-0"
              children="search"
            />
            <input
              ref={searchInputRef}
              className={cn(
                'placeholder:text-text-primary flex w-full border-none bg-transparent py-2.5 text-[15px] leading-5 tracking-[0.25px] outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
              )}
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <CommandList>
            <Virtuoso
              className={cn('min-h-[240px] overflow-x-hidden', {
                'h-[calc(50dvh-48px-46px-16px)]':
                  visibleFrequentOptions.length + visibleOptions.length > 5,
              })}
              data={visibleOptions}
              components={{
                Header: () => (
                  <>
                    <CommandEmpty>No results found.</CommandEmpty>
                    {visibleRecommendedOptions.length > 0 && (
                      <>
                        <CommandGroup
                          heading={
                            <div className="flex items-center gap-2">
                              <LocalIcon
                                name="opti"
                                size="18"
                              />
                              {recommendedGroupLabel ?? 'opti Suggested'}
                            </div>
                          }
                        >
                          {visibleRecommendedOptions.map((option) => {
                            const isSelected = selectedValues.includes(option.value)
                            return (
                              <MultiSelectOptionMemoized
                                key={option.value}
                                option={option}
                                isSelected={isSelected}
                                toggleOption={toggleOption}
                                searchValue={searchValue}
                                recommended
                                hideCheckbox={isSingle}
                              />
                            )
                          })}
                        </CommandGroup>
                      </>
                    )}
                    {visibleFrequentOptions.length > 0 && (
                      <>
                        <CommandGroup heading={frequentGroupLabel ?? 'Frequently Used'}>
                          {visibleFrequentOptions.map((option) => {
                            const isSelected = selectedValues.includes(option.value)
                            return (
                              <MultiSelectOptionMemoized
                                key={option.value}
                                option={option}
                                isSelected={isSelected}
                                toggleOption={toggleOption}
                                searchValue={searchValue}
                                recommended={recommendedValues.includes(option.value)}
                                hideCheckbox={isSingle}
                              />
                            )
                          })}
                        </CommandGroup>
                        <CommandSeparator className="my-1" />
                      </>
                    )}
                  </>
                ),
              }}
              itemContent={(index, option) => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <>
                    {index === 0 && allSelectable && !isSingle && (
                      <CommandItem
                        key="all"
                        className={cn({
                          'bg-primary-selected': selectedValues.length === options.length,
                        })}
                        onSelect={toggleAll}
                      >
                        <Icon
                          className={cn('text-content-high-emphasis text-[18px]', {
                            'text-primary-main': selectedValues.length === options.length,
                          })}
                          children={
                            selectedValues.length === options.length
                              ? 'check_box'
                              : 'check_box_outline_blank'
                          }
                          fill={selectedValues.length === options.length}
                        />
                        <Text appearance="BODY2">(Select All)</Text>
                      </CommandItem>
                    )}
                    <MultiSelectOptionMemoized
                      key={option.value}
                      option={option}
                      isSelected={isSelected}
                      toggleOption={toggleOption}
                      searchValue={searchValue}
                      recommended={recommendedValues.includes(option.value)}
                      hideCheckbox={isSingle}
                    />
                  </>
                )
              }}
            />
          </CommandList>
          {!isSingle && (
            <>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-end gap-2 px-4 py-2">
                  <Button
                    size="small"
                    onClick={handleClear}
                    disabled={selectedValues.length === 0}
                  >
                    Clear All
                  </Button>
                </div>
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
